import { useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  Spinner,
  Flex,
  Alert,
  AlertIcon,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  useColorModeValue,
  VStack,
  Divider,
  HStack,
} from '@chakra-ui/react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  Area,
} from 'recharts';
import { format, parseISO, getDay, isBefore, startOfDay } from 'date-fns';
import { useOptimizedUserStore as useUserStore } from '../store/optimizedUserStore';
import { useAuthStore } from '../store/authStore';
import AppLayout from '../components/layout/AppLayout';
import { roundToDecimal } from '../utils/dateUtils';
import { groupEntriesByMonthWithAllDays } from '../utils/dateUtils';
import { DailyEntry, DailyTarget } from '../types';

const getDayName = (dayIndex: number): string => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[dayIndex] || 'Unknown';
};

const ProgressOverviewPage = () => {
  const { loading: authLoading } = useAuthStore();
  const {
    profile,
    dailyEntries,
    dailyTargets,
    targetSummary,
    calculations,
    error,
    profileLoading,
    entriesLoading,
    targetsLoading,
    summaryLoading,
    vacationLoading,
  } = useUserStore();

  const isLoading = authLoading || profileLoading || entriesLoading || targetsLoading || summaryLoading || vacationLoading;

  const loggedColor = useColorModeValue('#4299E1', '#63B3ED');
  const targetColor = useColorModeValue('#805AD5', '#9F7AEA');
  const cardBg = useColorModeValue('white', 'gray.800');
  const progressTrackColor = useColorModeValue('gray.100', 'gray.600');
  const progressFilledColor = useColorModeValue('blue.500', 'blue.300');
  const subtleText = useColorModeValue('gray.600', 'gray.400');

  const cumulativeHoursData = useMemo(() => {
    if (!profile || !dailyEntries || !dailyTargets) return [];

    const monthlyBreakdown = groupEntriesByMonthWithAllDays(
      dailyEntries,
      profile.tracking_start_date,
      profile.tracking_end_date
    );
    const allDays = monthlyBreakdown.flatMap(month => month.days || []);
    if (allDays.length === 0) return [];

    const targetsByDate = new Map();
    dailyTargets.forEach((target: any) => {
      targetsByDate.set(target.target_date, target);
    });

    const avgRequired = targetSummary
      ? targetSummary.avg_daily_target
      : (calculations ? calculations.requiredAverageDailyHours : 0);

    const yearlyTarget = targetSummary
      ? targetSummary.yearly_target_hours
      : (profile ? profile.yearly_target_hours : 0);

    const startDate = new Date(profile.tracking_start_date);
    const endDate = new Date(profile.tracking_end_date);
    const totalTrackingPeriodDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    let cumulativeLogged = 0;
    let cumulativeTarget = 0;

    return allDays.map((day, index) => {
      const dailyTarget = targetsByDate.get(day.date);
      const logged = day.entry ? day.entry.billed_hours : 0;
      const dailyTargetValue = dailyTarget ? dailyTarget.required_daily_target : avgRequired;

      cumulativeLogged += logged;
      cumulativeTarget += dailyTargetValue;

      const currentDate = new Date(day.date);
      const daysElapsed = Math.ceil((currentDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      const progressRatio = daysElapsed / totalTrackingPeriodDays;
      const projectedIdealProgress = roundToDecimal(yearlyTarget * progressRatio);

      return {
        date: day.date,
        displayDate: format(parseISO(day.date), 'MMM d'),
        shortDate: format(parseISO(day.date), 'M/d'),
        dayNumber: index + 1,
        logged,
        dailyTarget: dailyTargetValue,
        cumulativeLogged: roundToDecimal(cumulativeLogged),
        cumulativeTarget: roundToDecimal(cumulativeTarget),
        projectedIdealProgress,
      };
    });
  }, [profile, dailyEntries, dailyTargets, targetSummary, calculations]);

  const workPatternAnalysis = useMemo(() => {
    if (!profile || !dailyEntries || dailyEntries.length === 0) return null;

    // Initialize dynamically based on entries, not just profile settings
    const hoursByDay: { [key: number]: { totalHours: number; count: number } } = {};

    dailyEntries.forEach((entry: DailyEntry) => {
      try {
        const entryDate = parseISO(entry.entry_date);
        const dayIndex = getDay(entryDate);

        // Initialize day if not present
        if (!hoursByDay[dayIndex]) {
          hoursByDay[dayIndex] = { totalHours: 0, count: 0 };
        }

        // Add hours regardless of whether it's an official "work day"
        hoursByDay[dayIndex].totalHours += entry.billed_hours;
        hoursByDay[dayIndex].count += 1;

      } catch (e) {
        console.error("Error parsing entry date for work pattern:", entry.entry_date, e);
      }
    });

    // Get all day indices that have entries
    const dayIndicesWithEntries = Object.keys(hoursByDay).map(Number).sort((a, b) => a - b);

    // Calculate averages only for days with entries
    const averages = dayIndicesWithEntries.map(dayIndex => ({
      dayName: getDayName(dayIndex),
      averageHours: hoursByDay[dayIndex].count > 0
        ? roundToDecimal(hoursByDay[dayIndex].totalHours / hoursByDay[dayIndex].count)
        : 0,
      count: hoursByDay[dayIndex].count
    }));

    // Filter out days with zero count if any somehow slipped through (shouldn't happen with current logic)
    return averages.filter(avg => avg.count > 0);
  }, [profile, dailyEntries]);

  const targetAchievementRate = useMemo(() => {
    if (!dailyTargets || dailyTargets.length === 0 || !dailyEntries) return null;

    const today = startOfDay(new Date());
    const entriesMap = new Map(dailyEntries.map(entry => [entry.entry_date, entry.billed_hours]));

    let pastWorkDaysCount = 0;
    let metTargetCount = 0;

    dailyTargets.forEach((target: DailyTarget) => {
      try {
        const targetDate = parseISO(target.target_date);
        if (target.is_work_day && isBefore(targetDate, today)) {
          pastWorkDaysCount++;
          const loggedHours = entriesMap.get(target.target_date) || 0;
          if (loggedHours >= target.required_daily_target) {
            metTargetCount++;
          }
        }
      } catch (e) {
        console.error("Error processing target for achievement rate:", target.target_date, e);
      }
    });

    if (pastWorkDaysCount === 0) return { rate: 0, daysConsidered: 0 };

    return {
      rate: roundToDecimal((metTargetCount / pastWorkDaysCount) * 100),
      daysConsidered: pastWorkDaysCount
    };
  }, [dailyTargets, dailyEntries]);

  const requiredPaceRemaining = useMemo(() => {
    if (!targetSummary || targetSummary.remaining_work_days <= 0 || targetSummary.remaining_hours <= 0) return null;
    return roundToDecimal(targetSummary.remaining_hours / targetSummary.remaining_work_days);
  }, [targetSummary]);

  const progressPercentage = targetSummary && targetSummary.yearly_target_hours > 0
    ? (targetSummary.total_billed_hours / targetSummary.yearly_target_hours) * 100
    : calculations?.progressPercentage ?? 0;

  const totalBilledHours = targetSummary
    ? targetSummary.total_billed_hours
    : calculations?.totalHoursBilled ?? 0;

  const yearlyTargetHours = targetSummary
    ? targetSummary.yearly_target_hours
    : profile?.yearly_target_hours ?? 0;

  const remainingHours = targetSummary
    ? targetSummary.remaining_hours
    : calculations?.remainingHoursNeeded ?? 0;

  const formattedProjectedDate = useMemo(() => {
    if (targetSummary?.projected_completion_date) {
      try {
        const date = parseISO(targetSummary.projected_completion_date);
        return format(date, 'MMM d, yyyy');
      } catch (e) { return "N/A"; }
    } else if (targetSummary && targetSummary.projected_completion_date === null) {
      return "N/A";
    }
    return "Calculating...";
  }, [targetSummary]);

  const trackingEndDateFormatted = profile
    ? format(parseISO(profile.tracking_end_date), 'MMM d, yyyy')
    : 'N/A';

  const projectionStatus = useMemo(() => {
    if (profile && targetSummary?.projected_completion_date) {
      try {
        const projectedDate = parseISO(targetSummary.projected_completion_date);
        const endDate = parseISO(profile.tracking_end_date);
        if (projectedDate > endDate) return 'behind';
      } catch (e) { }
    }
    return 'on-track';
  }, [targetSummary, profile]);

  if (isLoading) {
    return (
      <AppLayout>
        <Flex justifyContent="center" alignItems="center" height="60vh">
          <Spinner size="xl" />
        </Flex>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <Alert status="error"><AlertIcon />{error}</Alert>
      </AppLayout>
    );
  }

  if (!profile) {
    return (
      <AppLayout>
        <Alert status="warning"><AlertIcon />Profile data not found. Ensure setup is complete.</Alert>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box mb={6}>
        <Heading as="h1" size="lg" mb={1}>
          Progress Overview
        </Heading>
        <Text color={subtleText}>
          Your journey towards the {yearlyTargetHours} hour target.
        </Text>
      </Box>

      <VStack spacing={6} align="stretch">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <Card bg={cardBg} boxShadow="md">
            <CardBody>
              <Stat>
                <StatLabel color="gray.500">Total Hours Logged</StatLabel>
                <StatNumber>{roundToDecimal(totalBilledHours)}</StatNumber>
                <StatHelpText>of {yearlyTargetHours} target</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBg} boxShadow="md">
            <CardBody>
              <Stat>
                <StatLabel color="gray.500">Remaining Hours</StatLabel>
                <StatNumber>{roundToDecimal(remainingHours)}</StatNumber>
                <StatHelpText>
                  {targetSummary ? `${targetSummary.remaining_work_days} work days left` : ''}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBg} boxShadow="md">
            <CardBody>
              <Stat>
                <StatLabel color="gray.500">Projected Completion</StatLabel>
                <StatNumber color={projectionStatus === 'behind' ? 'red.500' : 'inherit'}>
                  {formattedProjectedDate}
                </StatNumber>
                <StatHelpText>Target End: {trackingEndDateFormatted}</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBg} boxShadow="md">
            <CardBody>
              <Stat>
                <StatLabel color="gray.500">Avg. Daily Target</StatLabel>
                <StatNumber>
                  {targetSummary ? roundToDecimal(targetSummary.avg_daily_target) : 'N/A'} hrs
                </StatNumber>
                <StatHelpText>Across all work days</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        <Card bg={cardBg} boxShadow="md">
          <CardHeader>
            <Heading size="md">Overall Progress</Heading>
          </CardHeader>
          <CardBody>
            <Flex align="center" mb={2}>
              <Text fontWeight="bold" fontSize="2xl" color={progressFilledColor} mr={3}>
                {roundToDecimal(progressPercentage)}%
              </Text>
              <Text color="gray.500">Complete</Text>
            </Flex>
            <Progress
              value={progressPercentage}
              size="lg"
              colorScheme="blue"
              bg={progressTrackColor}
              borderRadius="md"
              hasStripe
              isAnimated={progressPercentage < 100}
            />
            <Flex justify="space-between" mt={1} fontSize="xs" color="gray.500">
              <Text>Start: {format(parseISO(profile.tracking_start_date), 'MMM d, yyyy')}</Text>
              <Text>End: {trackingEndDateFormatted}</Text>
            </Flex>
          </CardBody>
        </Card>

        <Box>
          <Heading size="md" mb={4}>Deeper Insights</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {workPatternAnalysis && (
              <Card bg={cardBg} boxShadow="sm">
                <CardHeader pb={2}>
                  <Heading size="sm">Work Pattern (Avg Hours)</Heading>
                </CardHeader>
                <CardBody pt={2}>
                  {workPatternAnalysis.length > 0 ? (
                    <HStack spacing={4} justify="space-around" wrap="wrap">
                      {workPatternAnalysis.map(day => (
                        <Box key={day.dayName} textAlign="center" p={1}>
                          <Text fontSize="xs" color={subtleText}>{day.dayName}</Text>
                          <Text fontWeight="medium">{day.averageHours} hrs</Text>
                          <Text fontSize="xs" color={subtleText}>({day.count} days)</Text>
                        </Box>
                      ))}
                    </HStack>
                  ) : (
                    <Text fontSize="sm" color={subtleText}>No work days configured or insufficient data.</Text>
                  )}
                </CardBody>
              </Card>
            )}

            <Card bg={cardBg} boxShadow="sm">
              <CardHeader pb={2}>
                <Heading size="sm">Daily Performance</Heading>
              </CardHeader>
              <CardBody pt={2}>
                <VStack spacing={3} align="stretch">
                  {targetAchievementRate !== null ? (
                    <Stat>
                      <StatLabel color="gray.500">Daily Target Hit Rate</StatLabel>
                      <StatNumber>{targetAchievementRate.rate}%</StatNumber>
                      <StatHelpText>Met target on {Math.round(targetAchievementRate.rate / 100 * targetAchievementRate.daysConsidered)} of {targetAchievementRate.daysConsidered} past workdays</StatHelpText>
                    </Stat>
                  ) : (
                    <Text fontSize="sm" color={subtleText}>
                      {(!dailyTargets || dailyTargets.length === 0) && !targetsLoading
                        ? 'No daily target data available.'
                        : !dailyEntries && !entriesLoading
                        ? 'No daily entry data available.'
                        : 'Calculating target achievement...'}
                    </Text>
                  )}
                  <Divider />
                  {requiredPaceRemaining !== null ? (
                    <Stat>
                      <StatLabel color="gray.500">Required Pace Remaining</StatLabel>
                      <StatNumber
                        color={targetSummary && requiredPaceRemaining !== null ? (
                          requiredPaceRemaining < targetSummary.avg_daily_target ? 'green.500' :
                          requiredPaceRemaining > targetSummary.avg_daily_target ? 'red.500' :
                          'inherit'
                        ) : 'inherit'}
                      >
                        {requiredPaceRemaining} hrs/day
                      </StatNumber>
                      <StatHelpText>Avg needed on remaining workdays to hit target</StatHelpText>
                    </Stat>
                  ) : (
                    <Text fontSize="sm" color={subtleText}>Target likely met or no remaining workdays.</Text>
                  )}
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>
        </Box>

        <Card bg={cardBg} boxShadow="md">
          <CardHeader>
            <Heading size="md">Cumulative Hours Over Time</Heading>
            <Text fontSize="xs" color={subtleText} mt={1}>
              Showing logged hours vs. ideal linear progress towards the yearly target.
            </Text>
          </CardHeader>
          <CardBody>
            <Box height="350px">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={cumulativeHoursData}
                  margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis
                    dataKey="displayDate"
                    tick={{ fontSize: 10 }}
                    interval={Math.max(1, Math.floor(cumulativeHoursData.length / 12))}
                    angle={-30}
                    textAnchor="end"
                    height={40}
                  />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    width={40}
                    domain={[0, 'auto']}
                    label={{ value: 'Hours', position: 'insideLeft', angle: -90, dy: 10, fontSize: 10 }}
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      const val = typeof value === 'number' ? roundToDecimal(value) : value;
                      let label;
                      switch (name) {
                        case 'cumulativeLogged': label = 'Total Logged'; break;
                        case 'projectedIdealProgress': label = 'Ideal Pace'; break;
                        default: label = name;
                      }
                      return [`${val} hrs`, label];
                    }}
                    labelFormatter={(label, payload) => {
                      const dataItem = payload && payload.length > 0 ? payload[0].payload : null;
                      if (dataItem) {
                        try {
                          return format(parseISO(dataItem.date), 'EEEE, MMM d, yyyy');
                        } catch (e) {
                          return dataItem.date;
                        }
                      }
                      return label;
                    }}
                  />
                  <Legend verticalAlign="top" height={36} />
                  <Area
                    type="monotone"
                    dataKey="cumulativeLogged"
                    name="Total Logged Hours"
                    fill={loggedColor}
                    stroke={loggedColor}
                    fillOpacity={0.3}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    type="linear"
                    dataKey="projectedIdealProgress"
                    name="Ideal Progress Pace"
                    stroke={targetColor}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                  />
                  <ReferenceLine
                    y={yearlyTargetHours}
                    stroke="grey"
                    strokeDasharray="3 3"
                    strokeWidth={1}
                    label={{
                      value: 'Yearly Target',
                      position: 'insideTopRight',
                      fill: 'grey',
                      fontSize: 10
                    }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </CardBody>
        </Card>
      </VStack>
    </AppLayout>
  );
};

export default ProgressOverviewPage;
